export const EMPTY_VALUE = '---';

const PROGRAM_STATUS_LABELS = {
    0: 'New',
    1: 'Active',
    3: 'Suspended',
    4: 'Eliminated',
    5: 'Merged',
    6: 'Deleted',
    7: 'Consolidated',
};

const PROGRAM_STATUS_BADGE_CLASSES = {
    0: 'badge-info',
    1: 'badge-success',
    3: 'badge-warning',
    4: 'badge-danger',
    5: 'badge-secondary',
    6: 'badge-dark',
    7: 'badge-secondary',
};

const getStatusPresentation = statusId => ({
    label: PROGRAM_STATUS_LABELS[statusId] ?? EMPTY_VALUE,
    badgeClass: PROGRAM_STATUS_BADGE_CLASSES[statusId] ?? 'badge-secondary',
});

const yesNo = flag => (flag ? 'Yes' : 'No');

const formatShortDate = date => (date ? new Date(date).toLocaleDateString('en-US') : EMPTY_VALUE);

const formatCreditHours = (min, max) => {
    if (min == null && max == null) return EMPTY_VALUE;
    if (min !== max) return `${min ?? EMPTY_VALUE} - ${max ?? EMPTY_VALUE}`;
    return `${min}`;
};

const findCipLabel = (cips, cipCode) => cips.find(cip => cip.cipCode === cipCode)?.cipName || EMPTY_VALUE;

const buildHeaderViewModel = programDetails => {
    const status = getStatusPresentation(programDetails.programStatusId);
    return {
        status,
        statusType: programDetails.statusType || EMPTY_VALUE,
        teachOutPlan: programDetails.teachOutFlag == null ? EMPTY_VALUE : yesNo(programDetails.teachOutFlag),
    };
};

const buildDetailsViewModel = (programDetails, degreeLevelDesc, credential) => ({
    degreeCertificateLevel: degreeLevelDesc || EMPTY_VALUE,
    creditHourRequirement: formatCreditHours(
        programDetails.programCreditReqMin,
        programDetails.programCreditReqMax
    ),
    approvalDate: formatShortDate(programDetails.programApprovalDate),
    deliveryMode: programDetails.programDistanceEd?.approval || EMPTY_VALUE,
    natureOfOnlineProgram: programDetails.programDistanceEdOnline?.approval || EMPTY_VALUE,
    programUrl: credential?.programURL || null,
});

const buildClassificationsViewModel = (programDetails, credential, cips) => ({
    cipCode: programDetails.cipSixDigitCode || EMPTY_VALUE,
    cipTitle: programDetails.cipSixDigitCode ? findCipLabel(cips, programDetails.cipSixDigitCode) : EMPTY_VALUE,
    stemPff: yesNo(programDetails.programPffStemFlag),
    nextLevelJobs: yesNo(programDetails.workforceReadyGrantFlag),
    careerRelevance: yesNo(programDetails.careerRelevanceFlag),
    endYear: programDetails.programEndYear || EMPTY_VALUE,
    programKeywords: credential?.keywords || EMPTY_VALUE,
});

const buildLocationsViewModel = locations => ({
    locations: (locations ?? []).map(location => {
        const status = location.programStatusName
            ? {
                label: location.programStatusName,
                badgeClass: getStatusPresentation(location.statusId).badgeClass,
            }
            : getStatusPresentation(location.statusId);
        return {
            id: location.locationId,
            name: location.locationName,
            status,
        };
    }),
});

const buildDescriptionViewModel = credential => ({
    description: credential?.programDescription || EMPTY_VALUE,
});

const buildRequestHistoryViewModel = programDetails => ({
    comments: programDetails.programComments || EMPTY_VALUE,
});

const buildEnrollmentHistoryViewModel = (extraViewDetails, hasLoaded) => {
    if (!hasLoaded || !extraViewDetails) {
        return { isLoading: true, years: [] };
    }
    return {
        isLoading: false,
        years: [
            {
                label: extraViewDetails.fafsaYear1,
                headCount: extraViewDetails.year1Headcount,
                fte: extraViewDetails.year1FTE,
                degrees: extraViewDetails.year1Degrees,
            },
            {
                label: extraViewDetails.fafsaYear2,
                headCount: extraViewDetails.year2Headcount,
                fte: extraViewDetails.year2FTE,
                degrees: extraViewDetails.year2Degrees,
            },
            {
                label: extraViewDetails.fafsaYear3,
                headCount: extraViewDetails.year3Headcount,
                fte: extraViewDetails.year3FTE,
                degrees: extraViewDetails.year3Degrees,
            },
        ],
    };
};

// Adapter/mapping layer: the only place that knows the shape of the AcademicProgramView API
// response. Cards never see academicAffairsData directly - they only receive the narrow view
// model built here for their own section.
export const buildProgramDetailsViewModel = (academicAffairsData, cips, extraViewDetails, extraDetailsHasLoaded) => {
    if (!academicAffairsData?.programDetails) return null;

    const { programDetails, credential, degreeLevelDesc, locations, programInstitution } = academicAffairsData;

    return {
        header: {
            programId: programDetails.programId,
            programName: programDetails.programFormalName,
            institutionName: programInstitution?.locationName || EMPTY_VALUE,
            ...buildHeaderViewModel(programDetails),
        },
        details: buildDetailsViewModel(programDetails, degreeLevelDesc, credential),
        classifications: buildClassificationsViewModel(programDetails, credential, cips),
        locations: buildLocationsViewModel(locations),
        description: buildDescriptionViewModel(credential),
        requestHistory: buildRequestHistoryViewModel(programDetails),
        enrollment: buildEnrollmentHistoryViewModel(extraViewDetails, extraDetailsHasLoaded),
    };
};
